/**
 * Два дефиса подряд → длинное тире, как в редакторах текста: автор набирает «--»,
 * в поле встаёт «—». Работает во ВСЕХ полях, где текст набирает человек, из одного
 * места: глобальный обработчик ввода (`installEmDashTyping`) для `input`/`textarea`
 * и правило ввода TipTap (`EmDash` в `emDashExtension.ts`) для богатых редакторов.
 *
 * Только набор с клавиатуры (`insertText` дефиса): вставка из буфера и удаление
 * текст не трогают. Поля `url`/`email`/`password`/`number` пропускаются по типу, адрес,
 * набранный в обычном поле или в тексте, — по виду слова (`looksLikeAddress`);
 * точечно отключить можно атрибутом `data-no-typography` на поле или предке.
 */
export const EM_DASH = '—'

/**
 * Последнее слово перед курсором — адрес: в ссылках и почте «--» — часть адреса
 * (`xn--p1ai`, `/a--b`), а полей `type="url"` в студии нет — ссылки набирают в
 * обычных полях и прямо в тексте статьи.
 */
export const looksLikeAddress = (textBeforeCaret: string): boolean => {
  const word = textBeforeCaret.split(/\s/).pop() ?? ''
  return word.includes('://') || /^www\./i.test(word) || word.includes('@')
}

/** Заменяет `--` перед курсором; `null` — заменять нечего. */
export const applyEmDash = (
  value: string,
  caret: number,
): { value: string; caret: number } | null =>
  caret >= 2 && value.slice(caret - 2, caret) === '--' && !looksLikeAddress(value.slice(0, caret))
    ? { value: value.slice(0, caret - 2) + EM_DASH + value.slice(caret), caret: caret - 1 }
    : null

const TEXT_INPUT_TYPES = new Set(['text', 'search'])

const isTextField = (el: EventTarget | null): el is HTMLInputElement | HTMLTextAreaElement =>
  el instanceof HTMLTextAreaElement ||
  (el instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(el.type))

/**
 * Значение пишется НАТИВНЫМ сеттером прототипа, а не `el.value = …`: React
 * перехватывает сеттер у самого узла и запомнил бы новое значение как уже
 * увиденное — `onChange` не сработал бы, и в стейте осталось бы «--».
 */
const setNativeValue = (el: HTMLInputElement | HTMLTextAreaElement, value: string) => {
  const proto =
    el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value)
}

const onInput = (e: Event) => {
  if (!(e instanceof InputEvent) || e.inputType !== 'insertText' || e.data !== '-') return
  const el = e.target
  if (!isTextField(el) || el.readOnly || el.closest('[data-no-typography]')) return
  const caret = el.selectionStart
  if (caret === null || caret !== el.selectionEnd) return
  const next = applyEmDash(el.value, caret)
  if (!next) return
  setNativeValue(el, next.value)
  el.setSelectionRange(next.caret, next.caret)
}

/**
 * Ставится один раз на документ, в фазе ПЕРЕХВАТА: она идёт раньше обработчиков
 * React (они висят на корне приложения), и `onChange` любого поля уже читает
 * значение с тире. Возвращает снятие — для тестов.
 */
export const installEmDashTyping = (doc: Document = document): (() => void) => {
  doc.addEventListener('input', onInput, true)
  return () => doc.removeEventListener('input', onInput, true)
}
